import { useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  Crosshair,
  Gauge,
  Map,
  PlaneTakeoff,
  Sprout
} from "lucide-react";
import { ConceptBar } from "../components/ConceptBar";
import { DemoForm } from "../components/DemoForm";
import { MetricValue } from "../components/MetricValue";
import { ReactiveGrid } from "../components/ReactiveGrid";
import { useGsap } from "../hooks/useGsap";
import "../styles/field.css";

const modes = {
  health: {
    label: "Стресс растений",
    copy: "Выделяем зоны, где индекс растительности отклоняется от нормы.",
    className: "field-map--health",
  },
  moisture: {
    label: "Влажность",
    copy: "Сопоставляем неоднородность грунта с рельефом и историей полива.",
    className: "field-map--moisture",
  },
  route: {
    label: "Маршрут",
    copy: "Строим полёт с учётом ветра, батарей и безопасных точек возврата.",
    className: "field-map--route",
  }
} as const;

type ModeKey = keyof typeof modes;

const fieldTickerItems = ["RTK FIX", "NDVI 0.74", "42.8 KM", "12 SAT", "WIND 3.2 M/S", "684 HA", "17 ZONES"];

function MapTag({ x, y, width, children }: { x: number; y: number; width: number; children: string }) {
  return (
    <g className="field-map__tag" transform={`translate(${x} ${y})`}>
      <rect width={width} height="38" rx="2" />
      <text x="12" y="24">{children}</text>
    </g>
  );
}

function FieldMapOverlay({ mode }: { mode: ModeKey }) {
  return (
    <svg
      className={`field-map__overlay field-map__overlay--${mode}`}
      viewBox="0 0 1536 1024"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      {mode === "health" && (
        <>
          <path className="field-map__region field-map__region--anomaly" d="M648 42C724 65 805 48 862 103C907 147 894 222 842 274C790 324 698 309 647 248C602 194 596 91 648 42Z" />
          <path className="field-map__region field-map__region--anomaly" d="M518 398C610 374 731 392 804 450C862 496 844 558 777 598C701 644 584 622 535 565C495 518 476 430 518 398Z" />
          <path className="field-map__region field-map__region--anomaly" d="M712 694C823 646 983 666 1080 733C1143 778 1112 850 1024 891C918 941 759 918 699 852C658 807 659 735 712 694Z" />
          <path className="field-map__region field-map__region--anomaly" d="M1082 806C1198 761 1385 781 1498 854L1536 884V1024H1050C1019 951 1019 849 1082 806Z" />
          <MapTag x={682} y={78} width={174}>NDVI −13.8%</MapTag>
          <MapTag x={556} y={420} width={154}>СТРЕСС 04</MapTag>
          <MapTag x={794} y={728} width={168}>ОСМОТР 11</MapTag>
          <g className="field-map__marker" transform="translate(772 226)"><circle r="18" /><circle r="6" /></g>
          <g className="field-map__marker" transform="translate(720 536)"><circle r="18" /><circle r="6" /></g>
          <g className="field-map__marker" transform="translate(1014 806)"><circle r="18" /><circle r="6" /></g>
        </>
      )}

      {mode === "moisture" && (
        <>
          <path className="field-map__region field-map__region--parcel" d="M8 366H452V1016H8Z" />
          <path className="field-map__region field-map__region--parcel" d="M496 8H1528V334H496Z" />
          <path className="field-map__region field-map__region--parcel field-map__region--pivot" d="M496 366H1528V1016H496Z" />
          <path className="field-map__irrigation" d="M518 1006C601 695 863 478 1190 478C1325 478 1437 515 1528 566" />
          <path className="field-map__irrigation" d="M705 1016C777 785 975 632 1214 632C1330 632 1434 668 1528 729" />
          <MapTag x={174} y={414} width={202}>ПЕРЕУВЛАЖНЕНИЕ</MapTag>
          <MapTag x={710} y={66} width={158}>−9% ВЛАГИ</MapTag>
          <MapTag x={1072} y={612} width={176}>КОНТУР ПОЛИВА</MapTag>
          <g className="field-map__marker" transform="translate(322 702)"><circle r="18" /><circle r="6" /></g>
          <g className="field-map__marker" transform="translate(790 240)"><circle r="18" /><circle r="6" /></g>
          <g className="field-map__marker" transform="translate(1160 752)"><circle r="18" /><circle r="6" /></g>
        </>
      )}

      {mode === "route" && (
        <>
          <path className="field-map__route-shadow" d="M268 166L450 166L450 346L706 346C780 346 836 382 892 438L990 536H1280V820H734L578 674" />
          <path className="field-map__route" d="M268 166L450 166L450 346L706 346C780 346 836 382 892 438L990 536H1280V820H734L578 674" />
          <MapTag x={506} y={294} width={188}>МАРШРУТ 42.8 КМ</MapTag>
          <MapTag x={1090} y={770} width={154}>ВОЗВРАТ 02</MapTag>
          {[[268, 166], [450, 346], [990, 536], [1280, 820], [734, 820], [578, 674]].map(([x, y], index) => (
            <g className="field-map__marker" transform={`translate(${x} ${y})`} key={index}><circle r="18" /><circle r="6" /></g>
          ))}
        </>
      )}
    </svg>
  );
}

export function FieldPage() {
  const page = useRef<HTMLElement>(null);
  const [mode, setMode] = useState<ModeKey>("health");
  const [area, setArea] = useState(420);
  const [applications, setApplications] = useState(3);

  const estimate = useMemo(() => {
    const savedSolution = Math.round(area * applications * 1.4);
    const flightHours = Math.max(1, Math.round((area / 75) * 10) / 10);
    const missionPrice = Math.round((area * 92 + applications * 3800) / 100) * 100;
    return { savedSolution, flightHours, missionPrice };
  }, [area, applications]);

  useGsap(page, (gsap) => {
    gsap.from(".field-hero__content > *", {
      y: 32,
      opacity: 0,
      stagger: 0.09,
      duration: 0.9,
      ease: "power3.out"
    });
    gsap.from(".field-hero__telemetry > *, .field-hero__status", {
      y: 24,
      opacity: 0,
      stagger: 0.08,
      duration: 0.8,
      delay: 0.55,
      ease: "power3.out"
    });

    gsap.fromTo(
      ".field-hero__bg img",
      { scale: 1.1 },
      {
        scale: 1,
        ease: "none",
        scrollTrigger: {
          trigger: ".field-hero",
          start: "top top",
          end: "bottom top",
          scrub: true
        }
      }
    );
    gsap.from(".field-map", {
      clipPath: "inset(8% 8% 8% 8%)",
      duration: 1.25,
      ease: "power4.out",
      scrollTrigger: {
        trigger: ".field-platform",
        start: "top 72%"
      }
    });
    gsap.from(".field-map__marker", {
      scale: 0,
      opacity: 0,
      stagger: 0.18,
      duration: 0.65,
      ease: "back.out(2)",
      scrollTrigger: {
        trigger: ".field-map",
        start: "top 65%"
      }
    });

    const cards = gsap.utils.toArray<HTMLElement>(".field-process article");
    cards.forEach((card, index) => {
      gsap.from(card, {
        y: 90,
        opacity: 0,
        duration: 0.8,
        delay: index * 0.05,
        scrollTrigger: {
          trigger: card,
          start: "top 88%"
        }
      });
    });

    gsap.to(".field-process__progress i", {
      scaleX: 1,
      ease: "none",
      scrollTrigger: {
        trigger: ".field-process",
        start: "top 68%",
        end: "bottom 32%",
        scrub: 0.8,
      },
    });

    gsap.from(".field-live__bar i", {
      scaleY: 0,
      transformOrigin: "bottom",
      duration: 1.05,
      stagger: 0.06,
      ease: "power3.out",
      scrollTrigger: { trigger: ".field-live", start: "top 68%" },
      onComplete: () => {
        gsap.to(".field-live__bar i", {
          scaleY: (index) => 0.82 + ((index * 11) % 18) / 100,
          duration: 1.1,
          stagger: { each: 0.045, from: "center" },
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
        });
      },
    });
    gsap.from(".field-live__panel > *, .field-case", {
      opacity: 0,
      y: 34,
      stagger: 0.07,
      duration: 0.75,
      ease: "power3.out",
      scrollTrigger: { trigger: ".field-live", start: "top 72%" },
    });
    gsap.fromTo(".field-live__scan", {
      xPercent: -115,
    }, {
      xPercent: 285,
      duration: 3.8,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut",
    });
  });

  return (
    <main id="main" ref={page} className="field">
      <ConceptBar label="Агротехнологии" dark />

      <header className="field-nav">
        <a className="field-logo" href="#top">
          FIELD/OS
        </a>
        <nav aria-label="Основная навигация">
          <a href="#platform">Платформа</a>
          <a href="#process">Процесс</a>
          <a href="#calculator">Экономика</a>
        </nav>
        <a className="field-nav__cta" href="#contact" data-magnetic>
          Запросить вылет
        </a>
      </header>

      <section className="field-hero" id="top" aria-labelledby="field-title">
        <ReactiveGrid className="field-gravity-grid" color="194, 242, 61" cell={48} radius={210} strength={34} />
        <div className="field-hero__bg">
          <img
            src="/assets/field/mission.webp"
            alt="Агроном контролирует полёт профессионального дрона над полем"
            width="1800"
            height="1200"
            fetchPriority="high"
          />
        </div>
        <div className="field-hero__overlay" />
        <div className="field-hero__content">
          <p>Аэроразведка для хозяйств от 150 га</p>
          <h1 id="field-title">Поле видно до первого вылета</h1>
          <p>
            FIELD/OS объединяет съёмку, агрономическую разметку и план точечной обработки в одной
            понятной миссии
          </p>
          <div className="field-hero__actions">
            <a className="field-button" href="#calculator" data-magnetic>
              Рассчитать миссию <ArrowRight aria-hidden="true" />
            </a>
            <a href="#platform">Посмотреть платформу</a>
          </div>
        </div>
        <div className="field-hero__status">
          <span>Флот доступен</span>
          <b>Поволжье · Юг · ЦФО</b>
        </div>
        <div className="field-hero__telemetry" aria-label="Телеметрия текущей миссии">
          <div><span>NDVI mean</span><strong>0.74</strong><i>+8.2%</i></div>
          <div><span>Route</span><strong>42.8 km</strong><i>locked</i></div>
          <div><span>Signal</span><strong>RTK fix</strong><i>12 sat</i></div>
        </div>
      </section>

      <section className="field-proof" aria-label="Ключевые возможности">
        <div>
          <PlaneTakeoff aria-hidden="true" />
          <span>Один вылет</span>
          <strong>до 750 га</strong>
        </div>
        <div>
          <Crosshair aria-hidden="true" />
          <span>Разрешение</span>
          <strong>от 3 см/пикс.</strong>
        </div>
        <div>
          <Gauge aria-hidden="true" />
          <span>Отчёт</span>
          <strong>в течение 24 ч</strong>
        </div>
        <p>Финальные параметры зависят от рельефа, культуры, погоды и требуемого набора сенсоров.</p>
      </section>

      <div className="field-ticker" aria-hidden="true">
        <div className="field-ticker__track">
          {[0, 1].map((group) => (
            <div className="field-ticker__group" key={group}>
              {[0, 1].flatMap((cycle) => fieldTickerItems.map((item) => (
                <span key={`${group}-${cycle}-${item}`}>{item}<i>→</i></span>
              )))}
            </div>
          ))}
        </div>
      </div>

      <section className="field-live" aria-labelledby="field-live-title">
        <div className="field-live__visual">
          <img src="/assets/field/survey.webp" alt="Аэрофотосъёмка сельскохозяйственных полей" width="1800" height="1200" loading="lazy" />
          <i className="field-live__scan" aria-hidden="true" />
          <div className="field-live__target field-live__target--one"><span>ZONE 04</span><b>−13.8%</b></div>
          <div className="field-live__target field-live__target--two"><span>ZONE 11</span><b>+6.4%</b></div>
        </div>
        <div className="field-live__panel">
          <p>Live mission / K-184</p>
          <h2 id="field-live-title" data-split>От снимка до решения — за один рабочий день.</h2>
          <div className="field-live__metrics">
            <article><span>Покрыто</span><strong><MetricValue value={684} suffix=" га" /></strong><small>с 06:40</small></article>
            <article><span>Аномалий</span><strong><MetricValue value={17} /></strong><small>4 критические</small></article>
            <article><span>Экономия раствора</span><strong><MetricValue value={23.6} suffix="%" decimals={1} /></strong><small>прогноз</small></article>
          </div>
          <div className="field-live__chart" aria-label="График индекса вегетации по секторам">
            {[42, 58, 51, 73, 84, 66, 92, 76, 63, 88, 95, 81].map((height, index) => (
              <div className="field-live__bar" key={index}><i style={{ height: `${height}%` }} /><span>{String(index + 1).padStart(2, "0")}</span></div>
            ))}
          </div>
          <div className="field-live__legend"><span><i />NDVI</span><span><i />контрольная медиана</span><b>Последнее обновление 08:42:16</b></div>
        </div>
      </section>

      <section className="field-platform" id="platform">
        <div className="field-platform__intro">
          <p>Рабочая карта</p>
          <h2 data-split>Не дашборд ради дашборда.</h2>
          <p>
            Агроном переключает один и тот же участок между понятными слоями, отмечает зоны
            проверки и передаёт исполнителю готовый маршрут.
          </p>
        </div>
        <div className={`field-map ${modes[mode].className}`}>
          <img
            src="/assets/field/survey.webp"
            alt="Аэрофотоснимок сельскохозяйственных участков"
            width="1254"
            height="1254"
            loading="lazy"
          />
          <div className="field-map__toolbar" role="tablist" aria-label="Слои карты">
            {(Object.keys(modes) as ModeKey[]).map((key) => (
              <button
                type="button"
                role="tab"
                aria-selected={mode === key}
                key={key}
                onClick={() => setMode(key)}
              >
                {modes[key].label}
              </button>
            ))}
          </div>
          <div className="field-map__legend" aria-live="polite">
            <Map aria-hidden="true" />
            <div>
              <b>{modes[mode].label}</b>
              <p>{modes[mode].copy}</p>
            </div>
          </div>
          <div className="field-map__layer" key={mode} aria-hidden="true">
            <FieldMapOverlay mode={mode} />
          </div>
        </div>
      </section>

      <section className="field-cases" aria-label="Результаты хозяйств">
        <article className="field-case" data-spotlight>
          <img src="/assets/field/mission.webp" alt="Полевой специалист управляет агродроном" width="1800" height="1200" loading="lazy" />
          <div><span>Зерновые / Самарская область</span><h2>−18% расхода фунгицида за сезон.</h2><p>FIELD/OS выделил 11 неоднородных зон на 1 840 га и подготовил карту дифференцированной обработки.</p></div>
        </article>
        <article className="field-case" data-spotlight>
          <img src="/assets/field/aerial.webp" alt="Сельскохозяйственные участки с высоты" width="1254" height="1254" loading="lazy" />
          <div><span>Сады / Краснодарский край</span><h2>9 часов вместо трёх дней обхода.</h2><p>Тепловизионный пролёт нашёл разрыв линии полива до появления видимого стресса в кронах.</p></div>
        </article>
      </section>

      <section className="field-process" id="process" aria-labelledby="field-process-title">
        <div className="field-process__heading">
          <p>От запроса до карты</p>
          <h2 id="field-process-title" data-split>Одна миссия, четыре ясных этапа.</h2>
          <div className="field-process__progress" aria-hidden="true">
            <span>BRIEF</span><b><i /></b><span>ACTION MAP</span>
          </div>
        </div>
        <article data-spotlight>
          <span>01</span>
          <Sprout aria-hidden="true" />
          <h3>Контекст поля</h3>
          <p>Культура, фаза роста, история обработок и то, что беспокоит агронома сейчас.</p>
          <ul><li>границы и рельеф</li><li>история обработок</li><li>погодное окно</li></ul>
          <b className="field-process__status">бриф миссии · 20 минут</b>
        </article>
        <article data-spotlight>
          <span>02</span>
          <PlaneTakeoff aria-hidden="true" />
          <h3>Съёмка</h3>
          <p>RGB и мультиспектральный пролёт по согласованному окну погоды.</p>
          <ul><li>RTK-привязка</li><li>RGB + multispectral</li><li>контроль ветра</li></ul>
          <b className="field-process__status">до 750 га за вылет</b>
        </article>
        <article data-spotlight>
          <span>03</span>
          <Map aria-hidden="true" />
          <h3>Разметка</h3>
          <p>Не просто индекс: специалист отмечает зоны осмотра и объясняет приоритет.</p>
          <ul><li>кластеры стресса</li><li>комментарий агронома</li><li>шкала приоритета</li></ul>
          <b className="field-process__status">17 зон в среднем</b>
        </article>
        <article data-spotlight>
          <span>04</span>
          <CheckCircle2 aria-hidden="true" />
          <h3>План действия</h3>
          <p>Экспортируем маршрут, норму обработки и короткий отчёт для команды.</p>
          <ul><li>маршрут обхода</li><li>карта переменной нормы</li><li>PDF для команды</li></ul>
          <b className="field-process__status">готово менее чем за 24 часа</b>
        </article>
      </section>

      <section className="field-calculator" id="calculator">
        <div className="field-calculator__copy">
          <p>Расчёт миссии</p>
          <h2>Посчитайте масштаб миссии.</h2>
          <p>
            Настройте площадь и число обработок, чтобы получить предварительный масштаб работ.
            Координатор уточнит расчёт после проверки границ участка и погодного окна.
          </p>
        </div>
        <div className="field-calculator__controls">
          <label>
            <span>
              Площадь <output>{area} га</output>
            </span>
            <input
              type="range"
              min="150"
              max="2500"
              step="10"
              value={area}
              onChange={(event) => setArea(Number(event.target.value))}
            />
          </label>
          <label>
            <span>
              Обработок в сезоне <output>{applications}</output>
            </span>
            <input
              type="range"
              min="1"
              max="8"
              value={applications}
              onChange={(event) => setApplications(Number(event.target.value))}
            />
          </label>
          <div className="field-calculator__results">
            <div>
              <span>Ориентир времени полёта</span>
              <strong><MetricValue value={estimate.flightHours} suffix=" ч" decimals={estimate.flightHours % 1 === 0 ? 0 : 1} /></strong>
            </div>
            <div>
              <span>Потенциально нераспылённый раствор*</span>
              <strong><MetricValue value={estimate.savedSolution} suffix=" л" /></strong>
            </div>
            <div>
              <span>Оценка миссии</span>
              <strong><MetricValue value={estimate.missionPrice} suffix=" ₽" /></strong>
            </div>
          </div>
          <small>*Модель условная и не является обещанием экономии.</small>
          <a className="field-button" href="#contact">
            Обсудить участок <ArrowRight aria-hidden="true" />
          </a>
        </div>
      </section>

      <section className="field-contact" id="contact">
        <div>
          <p>Следующее погодное окно</p>
          <h2>Расскажите, что происходит на поле.</h2>
        </div>
        <DemoForm
          title="Запросить предварительный план"
          description="Координатор уточнит регион, культуру, срочность задачи и доступные даты вылета."
          submitLabel="Отправить задачу"
          successMessage="Задача передана координатору."
          tone="dark"
          fields={["name", "phone"]}
        />
      </section>

      <footer className="field-footer">
        <div>
          <b>FIELD/OS</b>
          <p>Аэросъёмка и агрономическая аналитика.</p>
        </div>
        <div>
          <a href="mailto:mission@field-os.example">mission@field-os.example</a>
          <p>Координационный центр: Самара, ул. Полевая, 18</p>
        </div>
      </footer>
    </main>
  );
}
